import { useMemo, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { CartesianGrid, ResponsiveContainer, Scatter, ScatterChart, Tooltip as RTooltip, XAxis, YAxis, ZAxis } from "recharts";
import { useReportData } from "./UseReportData";
import { ChartCard, Empty, KpiBand, PlanActualBars, Seg, SectionFrame, SimpleTable, StatusDot, StatusKey, fmt, fmtPct, fmtT, shortNum, statusOf, timeMetrics, useChartColors, varPct } from "./Reportkit";

/** Per-crew metrics derived from one CrewRow. */
export const crewMetrics = (r) => {
  const hours = { cal: r.cal, avail: r.avail, goh: r.goh, noh: r.noh, delay: 0, pCal: r.pCal, pAvail: r.pAvail, pGoh: r.pGoh, pNoh: r.pNoh, pDelay: 0 };
  const a = timeMetrics(hours), p = timeMetrics(hours, true);
  return {
    perShift: r.shifts ? r.actual / r.shifts : null, perShiftP: r.shifts ? r.plan / r.shifts : null,
    cycle: r.trips ? r.cycleMin / r.trips : null, payload: r.trips ? r.actual / r.trips : null,
    pa: a, paP: p, compliance: r.attendances ? (r.flhaCompliant / r.attendances) * 100 : null,
  };
};

function Body({ d }) {
  const c = useChartColors();
  const [sort, setSort] = useState("tonnes");
  const [q, setQ] = useState("");
  const crews = d.crews;
  const bars = crews.map((r) => ({ name: `Crew ${r.crew}`.replace("Crew Crew", "Crew"), plan: r.plan, actual: r.actual }));

  const metricRows = [
    ["Shifts worked", (r) => fmt(r.shifts)],
    ["Plan tonnes", (r) => fmtT(r.plan)],
    ["Actual tonnes", (r) => <b>{fmtT(r.actual)} <StatusDot status={statusOf(r.actual, r.plan)} size={12} /></b>],
    ["Variance %", (r) => fmtPct(varPct(r.actual, r.plan), 1, true)],
    ["Tonnes / shift", (r) => fmtT(crewMetrics(r).perShift)],
    ["Trips", (r) => fmt(r.trips)],
    ["Tonnes / trip", (r) => fmt(crewMetrics(r).payload, 1)],
    ["Cycle min / trip", (r) => fmt(crewMetrics(r).cycle, 1)],
    ["Truck PA (plan)", (r) => { const m = crewMetrics(r); return m.pa.cal ? <>{fmtPct(m.pa.pa, 1)} <span style={{ opacity: 0.6 }}>({fmt(m.paP.pa, 1)}%)</span> <StatusDot status={statusOf(m.pa.pa, m.paP.pa)} size={12} /></> : "—"; }],
    ["Truck UA (plan)", (r) => { const m = crewMetrics(r); return m.pa.cal ? <>{fmtPct(m.pa.ua, 1)} <span style={{ opacity: 0.6 }}>({fmt(m.paP.ua, 1)}%)</span> <StatusDot status={statusOf(m.pa.ua, m.paP.ua)} size={12} /></> : "—"; }],
    ["Truck OE (plan)", (r) => { const m = crewMetrics(r); return m.pa.cal ? <>{fmtPct(m.pa.oe, 1)} <span style={{ opacity: 0.6 }}>({fmt(m.paP.oe, 1)}%)</span> <StatusDot status={statusOf(m.pa.oe, m.paP.oe)} size={12} /></> : "—"; }],
    ["Breakdowns (unplanned h)", (r) => `${fmt(r.breakdowns)} (${fmt(r.unplannedHours, 1)} h)`],
    ["NPT reports (hours)", (r) => `${fmt(r.nptCount)} (${fmt(r.nptHours, 1)} h)`],
    ["FLHA compliance", (r) => fmtPct(crewMetrics(r).compliance, 1)],
  ].map(([metric, f]) => ({ metric, ...Object.fromEntries(crews.map((r) => [r.crew, f(r)])) }));

  const ops = useMemo(() => {
    const rows = d.operators.map((o) => ({ ...o, perShift: o.shifts ? o.tonnes / o.shifts : 0, cyc: o.trips ? o.cycleMin / o.trips : null, payload: o.trips ? o.tonnes / o.trips : null, compliance: o.attendances ? (o.flha / o.attendances) * 100 : null }));
    const s = { tonnes: (a, b) => b.tonnes - a.tonnes, perShift: (a, b) => b.perShift - a.perShift, cycle: (a, b) => (a.cyc ?? 1e9) - (b.cyc ?? 1e9) }[sort];
    return rows.filter((o) => !q || o.name.toLowerCase().includes(q.toLowerCase())).sort(s);
  }, [d, sort, q]);
  const max = Math.max(1, ...ops.map((o) => o.perShift));

  const dots = d.operators.filter((o) => o.trips && o.shifts).map((o) => ({ x: +(o.cycleMin / o.trips).toFixed(2), y: Math.round(o.tonnes / o.shifts), name: o.name, shifts: o.shifts }));

  return (
    <>
      <KpiBand items={crews.map((r) => {
        const m = crewMetrics(r);
        return { label: `Crew ${r.crew}`, value: fmtT(r.actual), sub: `${fmtT(m.perShift)}/shift · ${fmt(r.shifts)} shifts`, status: statusOf(r.actual, r.plan), delta: varPct(r.actual, r.plan) };
      })} />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 2fr" }, gap: 2 }}>
        <ChartCard title="Crew plan vs actual" sub="Crew = crew recorded on the dispatch shift"><PlanActualBars data={bars} /></ChartCard>
        <ChartCard title="Crew comparison" sub="Production, fleet time, events and safety for the shifts each crew worked">
          <SimpleTable max={520} cols={[{ key: "metric", label: "Metric", align: "left" }, ...crews.map((r) => ({ key: r.crew, label: `Crew ${r.crew}`.replace("Crew Crew", "Crew") }))]} rows={metricRows} />
        </ChartCard>
      </Box>
      <ChartCard title="Operator performance" sub="Haul-truck operators, attributed through the truck assignment for each shift"
        action={<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <TextField size="small" placeholder="Search operator…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ width: 190 }} />
          <Seg value={sort} onChange={setSort} options={[["tonnes", "Tonnes"], ["perShift", "t / shift"], ["cycle", "Fastest cycle"]]} />
        </Box>}>
        {ops.length === 0 ? <Empty /> : (
          <SimpleTable max={520} cols={[
            { key: "name", label: "Operator", align: "left", render: (o) => <b>{o.name || `User ${o.userId}`}</b> },
            { key: "crew", label: "Crew", align: "left" },
            { key: "shifts", label: "Shifts", render: (o) => fmt(o.shifts) },
            { key: "tonnes", label: "Tonnes", render: (o) => fmtT(o.tonnes) },
            { key: "perShift", label: "t / shift", render: (o) => <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>{fmtT(o.perShift)}<Box sx={{ width: Math.max(2, (o.perShift / max) * 60), height: 6, borderRadius: 1, bgcolor: "primary.main", opacity: 0.6 }} /></Box> },
            { key: "trips", label: "Trips", render: (o) => fmt(o.trips) },
            { key: "cyc", label: "Cycle min", render: (o) => fmt(o.cyc, 1) },
            { key: "payload", label: "t / trip", render: (o) => fmt(o.payload, 1) },
            { key: "compliance", label: "FLHA", render: (o) => fmtPct(o.compliance, 0) },
            { key: "npt", label: "NPT reports", render: (o) => fmt(o.npt) },
          ]} rows={ops} />
        )}
      </ChartCard>
      <ChartCard title="Output vs cycle time" sub="Each dot is an operator — top-left is fast and productive">
        {dots.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 24, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={c.grid} />
              <XAxis type="number" dataKey="x" name="Cycle" tick={c.tick} domain={["auto", "auto"]} label={{ value: "Average cycle (min)", position: "insideBottom", offset: -12, fill: c.muted, fontSize: 11 }} />
              <YAxis type="number" dataKey="y" name="t/shift" tick={c.tick} tickFormatter={shortNum} width={54} domain={["auto", "auto"]} />
              <ZAxis range={[60, 60]} />
              <RTooltip cursor={{ strokeDasharray: "3 3" }} formatter={(v, n) => [n === "Cycle" ? `${v} min` : fmtT(v), n]} labelFormatter={() => ""} content={({ active, payload }) => active && payload?.length ? (
                <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", p: 1, borderRadius: 1, fontSize: 12 }}><b>{payload[0].payload.name}</b><br />{fmtT(payload[0].payload.y)} / shift · {payload[0].payload.x} min · {payload[0].payload.shifts} shifts</Box>) : null} />
              <Scatter data={dots} fill={c.series[0]} fillOpacity={0.75} isAnimationActive={false} />
            </ScatterChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
      <StatusKey />
      <Typography variant="caption" color="text.secondary">
        Operator = the person assigned to the truck for that date and shift (a truck has one operator per shift). Operator output reflects the route and loader they were allocated, so compare like with like. Crew breakdown and NPT counts use the crew of the shift the event falls in.
      </Typography>
    </>
  );
}

export default function CrewSection({ req }) {
  const st = useReportData("crew", req);
  return (
    <SectionFrame eyebrow="People performance" title="Crew & Operators" desc="Crew-to-crew comparison and haul-truck operator performance, tied to events and safety" state={st}>
      {st.data && (st.data.crews.length ? <Body d={st.data} /> : <Empty>No crew data for the current filters</Empty>)}
    </SectionFrame>
  );
}